import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { handleApiBadges } from "@/lib/badges";
import { Plus, Trash2, Pencil, Sparkles, Loader2, Download, Save } from "lucide-react";
import { exportTrainingPlanPDF } from "@/lib/pdf-export";
import { REFERENCE_TYPES } from "@/lib/strength";
import {
  DISCIPLINES,
  getDiscipline,
  normalizeDiscipline,
  emptyExercise,
  normalizeExercise,
} from "@/lib/trainingDisciplines";
import DisciplinePicker from "@/components/training/DisciplinePicker";
import DisciplineBadge from "@/components/training/DisciplineBadge";
import SportRefsPanel, { sportRefsFromApi } from "@/components/training/SportRefsPanel";
import TrainingExerciseEditor, { ExerciseEditorHeader } from "@/components/training/TrainingExerciseEditor";

const DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const MAIN_LIFTS = ["squat", "bench", "deadlift"];

const emptyDay = (discipline = "strength") => ({ day: "Lundi", exercises: [emptyExercise(discipline)] });
const emptyForm = () => ({ student_id: "", title: "", description: "", discipline: "strength", days: [emptyDay()] });

function liftMaxDraftFromApi(maxes = []) {
  const draft = { squat: "", bench: "", deadlift: "", other: "" };
  const refs = { squat: "tm", bench: "tm", deadlift: "tm", other: "tm" };
  maxes.forEach((m) => {
    if (draft[m.lift] !== undefined) {
      draft[m.lift] = String(m.value_kg);
      refs[m.lift] = m.reference_type || "tm";
    }
  });
  return { values: draft, refs };
}

export default function TrainingPlans() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [loading, setLoading] = useState(false);

  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [showAi, setShowAi] = useState(false);

  const [liftMaxes, setLiftMaxes] = useState({ values: { squat: "", bench: "", deadlift: "", other: "" }, refs: { squat: "tm", bench: "tm", deadlift: "tm", other: "tm" } });
  const [liftMaxesApi, setLiftMaxesApi] = useState([]);
  const [liftMaxesLoading, setLiftMaxesLoading] = useState(false);
  const [savingMaxes, setSavingMaxes] = useState(false);

  const [sportRefs, setSportRefs] = useState({});
  const [sportRefsApi, setSportRefsApi] = useState([]);
  const [sportRefsLoading, setSportRefsLoading] = useState(false);

  const isPro = user?.subscription?.plan_type === "pro";
  const planDiscipline = normalizeDiscipline(form.discipline);
  const disciplineMeta = getDiscipline(planDiscipline);

  const fetchData = () => {
    api.get("/training-plans").then((r) => setPlans(r.data));
    api.get("/students").then((r) => setStudents(r.data));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchLiftMaxes = async (studentId) => {
    if (!studentId) {
      setLiftMaxes({ values: { squat: "", bench: "", deadlift: "", other: "" }, refs: { squat: "tm", bench: "tm", deadlift: "tm", other: "tm" } });
      setLiftMaxesApi([]);
      return;
    }
    setLiftMaxesLoading(true);
    try {
      const { data } = await api.get("/lift-maxes", { params: { student_id: studentId } });
      setLiftMaxesApi(data || []);
      setLiftMaxes(liftMaxDraftFromApi(data || []));
    } catch {
      setLiftMaxes({ values: { squat: "", bench: "", deadlift: "", other: "" }, refs: { squat: "tm", bench: "tm", deadlift: "tm", other: "tm" } });
      setLiftMaxesApi([]);
    } finally {
      setLiftMaxesLoading(false);
    }
  };

  const fetchSportRefs = async (studentId) => {
    if (!studentId) {
      setSportRefs({});
      setSportRefsApi([]);
      return;
    }
    setSportRefsLoading(true);
    try {
      const { data } = await api.get("/sport-refs", { params: { student_id: studentId } });
      setSportRefsApi(data || []);
      setSportRefs(sportRefsFromApi(data || []));
    } catch {
      setSportRefs({});
      setSportRefsApi([]);
    } finally {
      setSportRefsLoading(false);
    }
  };

  useEffect(() => {
    if (form.student_id) {
      fetchLiftMaxes(form.student_id);
      fetchSportRefs(form.student_id);
    }
  }, [form.student_id]);

  const resetForm = () => {
    setForm(emptyForm());
    setLiftMaxes({ values: { squat: "", bench: "", deadlift: "", other: "" }, refs: { squat: "tm", bench: "tm", deadlift: "tm", other: "tm" } });
    setLiftMaxesApi([]);
    setSportRefs({});
    setSportRefsApi([]);
  };

  const handleDisciplineChange = (discipline) => {
    const d = normalizeDiscipline(discipline);
    setForm((prev) => ({
      ...prev,
      discipline: d,
      days: [{ day: "Lundi", exercises: [emptyExercise(d)] }],
    }));
  };

  const openEdit = (plan) => {
    const discipline = normalizeDiscipline(plan.discipline || "strength");
    setEditing(plan.plan_id);
    setForm({
      student_id: plan.student_id,
      title: plan.title,
      description: plan.description || "",
      discipline,
      days: plan.days?.length
        ? plan.days.map((d) => ({
            ...d,
            exercises: (d.exercises || []).map((ex) => normalizeExercise(ex, discipline)),
          }))
        : [emptyDay(discipline)],
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, discipline: planDiscipline };
      if (editing) {
        await api.put(`/training-plans/${editing}`, payload);
        toast.success("Plan mis à jour");
      } else {
        const res = await api.post("/training-plans", payload);
        handleApiBadges(res, toast);
        toast.success("Plan créé");
      }
      setOpen(false);
      resetForm();
      setEditing(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    await api.delete(`/training-plans/${id}`);
    toast.success("Supprimé");
    fetchData();
  };

  const applyAiTraining = (ai) => {
    const discipline = normalizeDiscipline(ai.discipline || form.discipline);
    setForm((prev) => ({
      ...prev,
      discipline,
      title: ai.title || prev.title,
      description: ai.description || prev.description,
      days: ai.days?.length
        ? ai.days.map((d) => ({
            ...d,
            exercises: (d.exercises || []).map((ex) => normalizeExercise(ex, discipline)),
          }))
        : prev.days,
    }));
    setShowAi(false);
    setAiPrompt("");
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const res = await api.post("/ai/generate-training", {
        prompt: aiPrompt,
        student_id: form.student_id || undefined,
        discipline: planDiscipline,
      });
      applyAiTraining({ ...res.data, discipline: res.data.discipline || planDiscipline });
      toast.success("Programme généré par IA ! Vérifiez et ajustez si nécessaire.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de génération IA");
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiSubstitute = async () => {
    if (!aiPrompt.trim() || !editing) return;
    setAiLoading(true);
    try {
      const res = await api.post("/ai/substitute-exercises", { plan_id: editing, prompt: aiPrompt });
      applyAiTraining({ ...res.data, discipline: res.data.discipline || planDiscipline });
      toast.success("Exercices substitués par IA ! Vérifiez et enregistrez.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de substitution IA");
    } finally {
      setAiLoading(false);
    }
  };

  const saveLiftMaxes = async () => {
    if (!form.student_id) {
      toast.error("Sélectionnez un élève");
      return;
    }
    setSavingMaxes(true);
    try {
      const maxes = MAIN_LIFTS.map((lift) => ({
        lift,
        value_kg: liftMaxes.values[lift] || null,
        reference_type: liftMaxes.refs[lift] || "tm",
      }));
      const { data } = await api.put("/lift-maxes", { student_id: form.student_id, maxes });
      setLiftMaxesApi(data || []);
      toast.success("Références force enregistrées");
      fetchLiftMaxes(form.student_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSavingMaxes(false);
    }
  };

  const addDay = () =>
    setForm({
      ...form,
      days: [...form.days, { day: DAYS[form.days.length % 7], exercises: [emptyExercise(planDiscipline)] }],
    });

  const updateDay = (di, field, value) => {
    const days = [...form.days];
    days[di] = { ...days[di], [field]: value };
    setForm({ ...form, days });
  };

  const addExercise = (di) => {
    const days = [...form.days];
    days[di].exercises = [...days[di].exercises, emptyExercise(planDiscipline)];
    setForm({ ...form, days });
  };

  const updateExercise = (di, ei, exercise) => {
    const days = [...form.days];
    days[di].exercises[ei] = exercise;
    setForm({ ...form, days });
  };

  const removeExercise = (di, ei) => {
    const days = [...form.days];
    days[di].exercises = days[di].exercises.filter((_, i) => i !== ei);
    setForm({ ...form, days });
  };

  const removeDay = (di) => setForm({ ...form, days: form.days.filter((_, i) => i !== di) });

  const studentName = (id) => students.find((s) => s.user_id === id)?.name || "N/A";

  const maxesForPreview = liftMaxesApi.length
    ? liftMaxesApi
    : MAIN_LIFTS.filter((l) => liftMaxes.values[l])
        .map((lift) => ({
          lift,
          value_kg: parseFloat(liftMaxes.values[lift]),
          reference_type: liftMaxes.refs[lift] || "tm",
        }));

  return (
    <div data-testid="training-plans-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase text-slate-900">Plans Entraînement</h1>
          <p className="text-slate-500 mt-1">
            {plans.length} plan{plans.length > 1 ? "s" : ""}
          </p>
        </div>
        <Dialog
          open={open}
          onOpenChange={(v) => {
            setOpen(v);
            if (!v) {
              resetForm();
              setEditing(null);
              setShowAi(false);
              setAiPrompt("");
            }
          }}
        >
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-full" data-testid="create-training-plan-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau plan
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="font-heading text-xl">{editing ? "Modifier" : "Nouveau plan d'entraînement"}</DialogTitle>
                {isPro && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5 text-purple-600 border-purple-200 hover:bg-purple-50"
                    onClick={() => setShowAi(!showAi)}
                  >
                    <Sparkles className="w-4 h-4" /> {editing ? "Modifier avec IA" : "Générer avec IA"}
                  </Button>
                )}
              </div>
            </DialogHeader>

            {showAi && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-100">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-semibold text-purple-900">Génération IA</span>
                  <Badge className="bg-purple-100 text-purple-700 text-[10px]">PRO</Badge>
                  <DisciplineBadge discipline={planDiscipline} size="sm" />
                </div>
                <Textarea
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={
                    editing
                      ? "Décrivez la blessure ou contrainte...\nEx: Tendinite au coude droit, remplace les exercices de triceps isolés"
                      : `Décrivez le profil et objectifs (${disciplineMeta.label})...\nEx: Programme ${disciplineMeta.shortLabel.toLowerCase()} adapté au niveau de l'élève`
                  }
                  className="bg-white min-h-[80px] text-sm"
                />
                <Button
                  type="button"
                  disabled={aiLoading || !aiPrompt.trim()}
                  onClick={editing ? handleAiSubstitute : handleAiGenerate}
                  className="mt-3 bg-purple-600 hover:bg-purple-700 text-white rounded-full gap-2 w-full"
                >
                  {aiLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> {editing ? "Modification en cours..." : "Génération en cours..."}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" /> {editing ? "Modifier le programme" : "Générer le programme"}
                    </>
                  )}
                </Button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label>Élève</Label>
                <Select value={form.student_id} onValueChange={(v) => setForm({ ...form, student_id: v })}>
                  <SelectTrigger className="mt-1.5 h-11" data-testid="training-student-select">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {students.map((s) => (
                      <SelectItem key={s.user_id} value={s.user_id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="mb-2 block">Discipline</Label>
                <DisciplinePicker value={planDiscipline} onChange={handleDisciplineChange} />
              </div>

              {form.student_id && disciplineMeta.usesLiftMaxes && (
                <div className="p-4 bg-slate-900 rounded-xl text-white space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold uppercase tracking-wide text-slate-300">Références force (SBD)</p>
                    {liftMaxesLoading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {MAIN_LIFTS.map((lift) => (
                      <div key={lift}>
                        <Label className="text-slate-400 text-xs capitalize">{lift}</Label>
                        <div className="flex gap-1.5 mt-1">
                          <Input
                            type="number"
                            step="0.5"
                            value={liftMaxes.values[lift]}
                            onChange={(e) =>
                              setLiftMaxes((prev) => ({
                                ...prev,
                                values: { ...prev.values, [lift]: e.target.value },
                              }))
                            }
                            placeholder="kg"
                            className="h-9 bg-slate-800 border-slate-700 text-white"
                          />
                          <Select
                            value={liftMaxes.refs[lift]}
                            onValueChange={(v) =>
                              setLiftMaxes((prev) => ({
                                ...prev,
                                refs: { ...prev.refs, [lift]: v },
                              }))
                            }
                          >
                            <SelectTrigger className="w-24 h-9 bg-slate-800 border-slate-700 text-white text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {REFERENCE_TYPES.map((r) => (
                                <SelectItem key={r.value} value={r.value}>
                                  {r.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={saveLiftMaxes}
                    disabled={savingMaxes}
                    className="gap-1.5"
                  >
                    {savingMaxes ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                    Enregistrer les max
                  </Button>
                </div>
              )}

              {form.student_id && disciplineMeta.usesSportRefs && (
                <div className="relative">
                  {sportRefsLoading && (
                    <div className="absolute top-3 right-3">
                      <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                    </div>
                  )}
                  <SportRefsPanel
                    studentId={form.student_id}
                    discipline={planDiscipline}
                    values={sportRefs}
                    onChange={setSportRefs}
                    onSaved={(data) => {
                      setSportRefsApi(data || []);
                      setSportRefs(sportRefsFromApi(data || []));
                    }}
                  />
                </div>
              )}

              <div>
                <Label>Titre</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  className="mt-1.5 h-11"
                  data-testid="training-title-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Jours</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addDay}>
                    <Plus className="w-3 h-3 mr-1" /> Jour
                  </Button>
                </div>
                {form.days.map((day, di) => (
                  <div key={di} className="p-3 bg-slate-50 rounded-xl mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <Select value={day.day} onValueChange={(v) => updateDay(di, "day", v)}>
                        <SelectTrigger className="w-40 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {DAYS.map((d) => (
                            <SelectItem key={d} value={d}>
                              {d}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeDay(di)} className="text-red-400 hover:text-red-600">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>

                    <ExerciseEditorHeader planDiscipline={planDiscipline} />

                    {day.exercises.map((ex, ei) => (
                      <TrainingExerciseEditor
                        key={ei}
                        exercise={ex}
                        planDiscipline={planDiscipline}
                        onUpdate={(updated) => updateExercise(di, ei, updated)}
                        onRemove={() => removeExercise(di, ei)}
                        liftMaxes={maxesForPreview}
                        sportRefs={sportRefsApi}
                      />
                    ))}
                    <Button type="button" variant="ghost" size="sm" onClick={() => addExercise(di)} className="text-xs text-blue-600">
                      + Exercice
                    </Button>
                  </div>
                ))}
              </div>

              <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full h-11" data-testid="submit-training-plan-btn">
                {loading ? "Enregistrement..." : editing ? "Mettre à jour" : "Créer le plan"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 mt-6">
        {plans.length === 0 ? (
          <p className="text-center text-slate-400 py-12">Aucun plan</p>
        ) : (
          plans.map((p) => (
            <Card key={p.plan_id} className="border-slate-100 hover:shadow-sm transition-shadow" data-testid={`training-plan-${p.plan_id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-900">{p.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{p.description}</p>
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <DisciplineBadge discipline={p.discipline || "strength"} size="sm" />
                      <Badge variant="secondary">{studentName(p.student_id)}</Badge>
                      <Badge variant="outline">{p.days?.length || 0} jours</Badge>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => exportTrainingPlanPDF(p)} title="Exporter PDF">
                      <Download className="w-4 h-4 text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openEdit(p)}>
                      <Pencil className="w-4 h-4 text-slate-400" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(p.plan_id)}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}